import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CmsProductReferencesComponent, isNotNullable, Product, ProductReference, ProductReferenceService, UrlModule } from '@spartacus/core';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CarouselModule, CmsComponentData, CurrentProductService, MediaModule } from "@spartacus/storefront";
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GaItem, GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { GenProductGridItemComponent } from '../product-list/product-grid-item/product-grid-item.component';

@Component({
  selector: 'gen-product-references',
  templateUrl: './product-references.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CarouselModule,
    MediaModule,
    RouterModule,
    UrlModule,
    GenProductGridItemComponent,
  ],
})
export class GenProductReferencesComponent {
  tooltipMessage: string;
  phoneOrEmail: string;
  gaItems: GaItem[] = [];
  gaListName: string;

  private subscription = new Subscription();

  constructor(
    protected cmsComponentData: CmsComponentData<CmsProductReferencesComponent>,
    protected currentProductService: CurrentProductService,
    private baseStoreService: GenBaseStoreService,
    private cdr: ChangeDetectorRef,
    protected productReferenceService: ProductReferenceService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
  ) {
    this.subscription.add(
      combineLatest([
        this.baseStoreService.getTooltipMessage(),
        this.baseStoreService.getPhoneNumber(),
      ]).subscribe(([messsage, phoneOrEmail]) => {
        this.tooltipMessage = messsage?.message;
        this.phoneOrEmail = phoneOrEmail?.contactInfo;
        this.cdr.markForCheck();
      })
    );
  }

  protected get componentData$(): Observable<CmsProductReferencesComponent> {
    return this.cmsComponentData.data$.pipe(filter((data) => Boolean(data)));
  }

  /**
   * Returns an Observable String for the product code
   */
  protected get productCode$(): Observable<string> {
    return this.currentProductService.getProduct().pipe(
      filter(isNotNullable),
      map((product) => product.code ?? ''),
      tap((_) => this.productReferenceService.cleanReferences())
    );
  }

  /**
   * Returns an Observable String for the title
   */
  get title$(): Observable<string | undefined> {
    return this.componentData$.pipe(map((data) => data?.title));
  }

  /**
   * Observable with an Array of Observables. This is done so that
   * the component UI could consider to lazy load the UI components when they're
   * in the viewpoint.
   */
  items$: Observable<Observable<Product | undefined>[]> =
    this.productCode$.pipe(
      distinctUntilChanged(),
      withLatestFrom(this.componentData$),
      tap(([productCode, data]) => {
        this.gaListName = data.title;
        return this.productReferenceService.loadProductReferences(
          productCode,
          data.productReferenceTypes
        )
      }
      ),
      switchMap(([productCode, data]) =>
        this.getProductReferences(productCode, data.productReferenceTypes ?? '')
      )
    );

  /**
   * Returns an observable for product references
   */
  private getProductReferences(
    code: string,
    referenceType: string
  ): Observable<Observable<Product | undefined>[]> {
    return this.productReferenceService
      .getProductReferences(code, referenceType)
      .pipe(
        filter((references) => Boolean(references)),
        map((references: ProductReference[]) => {
          if (references.length > 0) {
            this.googleAnalyticsService.sendViewItemListEvent(
              references.map(ref => ref.target),
              this.gaListName
            );
          }

          return references.map((reference) => of(reference.target));
        }),

      );
  }
}
